import { useEffect, useRef } from 'react';
import { instrumentApi } from '../../../../utils/api';
import { getInsightsSessionId } from '../utils/sessionId';

/**
 * Fire a single ``view`` track event AFTER the insight detail has loaded
 * successfully. Calling for a still-loading or 404 insight would create
 * orphan tracking rows; tying the fire to a confirmed-loaded insight
 * keeps the event log clean.
 *
 * Errors are logged via ``console.warn`` (best-effort, never bubbled to
 * the UI) so dropped tracking calls are visible during dev / monitoring
 * but do not break the page render.
 *
 * The internal ref guards against double-fire from React 18 StrictMode
 * dev re-mounts; backend Redis dedup (30 s SET NX) is the canonical
 * defence against F5 spam.
 */
export default function useTrackInsightView(insight) {
    const firedFor = useRef(null);

    useEffect(() => {
        const id = insight?.id;
        if (!id) return;
        if (firedFor.current === id) return;
        firedFor.current = id;

        const sessionId = getInsightsSessionId();
        instrumentApi
            .trackInsightEvent(id, { event_type: 'view', session_id: sessionId })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('[insights] track view failed', { id, error: err?.message || err });
            });
    }, [insight?.id]);
}
